import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  api,
  type BulkResult,
  type SortDirection,
  type TicketSort,
} from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { filtersFromParams, withFilters } from "../lib/ticket-filters";
import { useIsDesktop } from "../lib/media";
import { clearNewTicketDraft, useDraftIds, useUnsentNewTickets } from "../lib/drafts";
import { useBusinessCalendar, useLiveSla } from "../lib/sla";
import { markTicketReadInLists } from "../lib/socket";
import {
  isViewId,
  labelForView,
  paramsForView,
  userIdFromView,
  type ViewId,
} from "../lib/views";
import { cn } from "../components/ui/cn";
import { EmptyState } from "../components/EmptyState";
import { NewTicketDialog } from "../components/NewTicketDialog";
import { FilterBar, type Filters } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import { PrintThreadButton } from "../components/PrintThreadButton";
import { TicketDetail } from "../components/TicketDetail";
import { TicketDetailsPane } from "../components/list/TicketDetailsPane";
import { TicketListPane } from "../components/list/TicketListPane";
import { PreviewList } from "../components/PreviewList";
import { ResizeHandle } from "../components/ui/ResizeHandle";
import { usePaneSize } from "../lib/pane-size";
import { useThreadParticipants } from "../lib/participants";
import type { BusinessCalendar } from "@helpdesk/sla";
import { SlaLeft } from "../components/SlaClock";
import type { Pipeline, TicketSummary, User } from "../types";

/**
 * Rows per request. The server allows up to 200; 100 is a page that renders
 * fast enough to feel instant and is deep enough that most views need only one.
 */
const PAGE_SIZE = 100;

/**
 * Where offset paging stops, matching the server's own ceiling on `offset`
 * (tickets.controller.ts). Only the non-default sorts can reach it, and a queue
 * 10,000 rows deep is a search, not a scroll.
 */
const MAX_OFFSET = 10_000;

/**
 * One screen per view, in one of two layouts.
 *
 * The layout is not a setting -- it follows what the agent is doing:
 *
 *   list      no ticket open. The full-width table, which is the shape you
 *             want for scanning, triaging and sorting.
 *   preview   a ticket is open. The familiar three-pane reading layout:
 *             conversations left, thread centre, ticket detail right.
 *
 * Deriving it from the selection rather than a toggle removes a mode nobody
 * should have to think about, and it means the table never shares a screen with
 * a conversation -- which is what previously left its columns running
 * underneath the thread.
 */
export function TicketsView({ user }: { user: User }) {
  const params = useParams();
  const view = params.viewId;

  if (!view || !isViewId(view)) return <Navigate to="/v/open" replace />;
  return <ViewBody key={view} view={view} user={user} />;
}

function ViewBody({ view, user }: { view: ViewId; user: User }) {
  const queryClient = useQueryClient();
  const calendar = useBusinessCalendar();
  const navigate = useNavigate();

  const routeTicketId = Number(useParams().ticketId);
  const selectedId = Number.isFinite(routeTicketId) ? routeTicketId : null;

  /** An open ticket is what puts the screen into the preview layout. */
  const inPreview = selectedId !== null;

  /*
   * Both the filters and the search term are read out of the query string,
   * which is what lets them outlive the remount a view change causes. See
   * ticket-filters.ts for why the URL and not per-browser storage.
   *
   * The search term is read, never owned: the box lives in the sidebar so it
   * can be used from pages that have no ticket list, and it publishes to `?q=`.
   * It is debounced before it reaches the URL, so what arrives here has already
   * settled and needs no second delay.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const debouncedSearch = (searchParams.get("q") ?? "").trim();

  const filters = useMemo(
    () => filtersFromParams(searchParams),
    [searchParams],
  );

  /*
   * Replacing rather than pushing, for the same reason the search box does:
   * settling on a pipeline is two or three goes at a dropdown, and each one
   * would otherwise become a history entry between here and wherever the agent
   * came from.
   */
  const setFilters = useCallback(
    (next: Filters) =>
      setSearchParams((current) => withFilters(current, next), {
        replace: true,
      }),
    [setSearchParams],
  );
  const [sort, setSort] = useState<TicketSort>("last_message");
  const [dir, setDir] = useState<SortDirection>("desc");
  const [showLeft, setShowLeft] = useState(true);
  const [showRight, setShowRight] = useState(true);
  /*
   * The details panel on a phone, where it is a sheet over the thread rather
   * than a column beside it -- so it starts closed, and its own state is kept
   * separate from the desktop toggle rather than sharing one that would open
   * a full-screen overlay the moment the window narrowed.
   */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isDesktop = useIsDesktop();

  /* Its own instance, for resuming the unsent new ticket from the bar below.
     The sidebar has one too; they read and write the same stored draft. */
  /*
   * The new-ticket dialog: closed, writing a fresh one, or resuming an unsent
   * one by its id. A string rather than a second piece of state, so "which
   * draft" cannot get out of step with "is it open".
   */
  const [composing, setComposing] = useState<
    null | { draftId?: string }
  >(null);

  /*
   * The Drafts view's contents, which no query can produce.
   *
   * An unsent reply lives in this browser, so the list of tickets that have
   * one is read here and handed to the server as ids -- see `paramsForView`.
   * Everything after that is the ordinary queue: the same columns, the same
   * SLA clock, the same assignee dropdown, because a draft is on a ticket and
   * a second kind of list would be a second thing to learn.
   *
   * Subscribed, not polled. Both hooks change identity only when the *set* of
   * drafts moves, so typing into a reply on another screen does not turn this
   * list's query key over -- see lib/drafts.ts.
   */
  const draftsView = view === "drafts";
  const draftIds = useDraftIds();
  const unsentNew = useUnsentNewTickets();

  const viewParams = useMemo(
    () => (draftsView ? { ids: draftIds } : paramsForView(view)),
    [draftsView, draftIds, view],
  );

  const searching = debouncedSearch.length > 0;

  /*
   * A per-agent view already fixes the assignee, so offering the control would
   * let someone contradict the view they just opened.
   *
   * Now that the filter travels between views it also has to be ignored on
   * those, not merely hidden: an assignee carried into "Unassigned" would
   * quietly turn it into somebody's queue, with no control on screen to undo
   * it. Ignored rather than dropped, so it is still there on the way back --
   * moving through a view that cannot use a filter is not a decision to
   * abandon it.
   */
  const viewFixesAssignee =
    /* Not while searching: a search leaves the view's scope behind (below),
       so there is no fixed assignee left to contradict. */
    !searching && (userIdFromView(view) !== null || view === "unassigned");

  const listParams = useMemo(
    () => ({
      /*
       * A search is a question about every ticket, so it leaves the view
       * behind entirely -- its status, and its scope too: "Mine",
       * "Unassigned", a team, a saved view, the drafts list. Dropping only the
       * status, as this first did, meant the same words found different
       * tickets depending on which item in the sidebar happened to be lit,
       * under a line saying "Searching all tickets". The ticket being closed,
       * or somebody else's, is the most likely reason anybody is searching for
       * it rather than looking at their queue.
       *
       * Spam is the one exception. Every other list hides it, and an agent
       * searching from inside the Spam view is checking what the filter took,
       * so there the search stays in spam.
       *
       * The filters an agent set by hand -- pipeline, assignee -- still apply
       * below. They are on screen next to the results, and clearing them is
       * one click; the view's scope was neither.
       */
      ...(searching
        ? { search: debouncedSearch, ...(view === "spam" ? viewParams : {}) }
        : viewParams),
      ...(filters.pipeline !== undefined ? { pipeline: filters.pipeline } : {}),
      ...(typeof filters.channel === "number"
        ? { channelId: filters.channel }
        : filters.channel !== undefined
          ? { channelType: filters.channel }
          : {}),
      ...(filters.assignee !== undefined && !viewFixesAssignee
        ? // An explicit assignee filter replaces the view's scope, otherwise
          // "my open tickets" filtered to someone else would return nothing.
          { assignee: filters.assignee, scope: undefined }
        : {}),
      sort,
      dir,
      limit: PAGE_SIZE,
    }),
    [
      viewParams,
      view,
      filters,
      viewFixesAssignee,
      sort,
      dir,
      searching,
      debouncedSearch,
    ],
  );

  /*
   * Mirrors the condition in tickets.service.ts that decides whether a cursor
   * is issued. Kept as one named value rather than repeated inline, because the
   * two places that need it must not drift apart.
   *
   * An SLA filter takes the list down the computed path, which never issues a
   * cursor whatever the sort -- so without this the Overdue view would read a
   * null cursor on the default sort as the end of the list and stop at one
   * page. A saved view carrying its own SLA filter has the same shape, and this
   * cannot see it: its filters are resolved server-side by design, so that one
   * stops at a page until the server says which mode it used.
   */
  const cursorPaged =
    !listParams.slaState && sort === "last_message" && dir === "desc";

  /*
   * Paged, not capped.
   *
   * The server has always returned a cursor and the list has always asked for
   * exactly one page, so every view stopped dead at PAGE_SIZE rows with no way
   * to reach the rest -- on a busy queue that is most of it.
   *
   * Two paging modes, because the server offers two and the choice is not
   * ours: a keyset cursor is only correct for the default newest-first order,
   * whose tie break is the id the cursor walks. Sorting by any other column
   * pages by offset instead -- see the note on `nextCursor` in
   * tickets.service.ts, which explains why a cursor on a non-unique column
   * skips or repeats the rows that share a value.
   *
   * Offset paging is the weaker of the two and is not made to look otherwise:
   * a ticket arriving mid-scroll shifts every later row, so a sorted-by-subject
   * queue can show a duplicate. The server caps `offset` at 10,000 and this
   * stops there rather than asking for a page it knows will 400.
   */
  const list = useInfiniteQuery({
    queryKey: queryKeys.tickets(listParams),
    queryFn: ({ pageParam }) => api.tickets({ ...listParams, ...pageParam }),
    initialPageParam: {} as { cursor?: number; offset?: number },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.nextCursor !== null) return { cursor: lastPage.nextCursor };

      /*
       * A null cursor on the cursor-paged sort is the end of the list. On any
       * other sort it means "this sort has no cursor", which is not the same
       * thing, so fall through to offset.
       */
      if (cursorPaged) return undefined;
      if (lastPage.tickets.length < PAGE_SIZE) return undefined;

      const loaded = pages.reduce((sum, page) => sum + page.tickets.length, 0);
      return loaded >= MAX_OFFSET ? undefined : { offset: loaded };
    },
    staleTime: 30_000,
    /*
     * A search keeps what is on screen until its answer arrives.
     *
     * Every pause in typing settles a new `?q=` and so a new query key, and a
     * new key starts with no data -- so the list dropped to its loading state
     * and back once per word, which reads as the results flashing. Holding the
     * previous rows makes refining a search a change of rows, not a blink.
     *
     * Only while searching. Changing view is a different question, and the old
     * queue's tickets sitting under the new view's name, even briefly, would be
     * a list that says something false.
     */
    placeholderData: (previous) => (searching ? previous : undefined),
  });

  /*
   * Stable, so ListEnd's observer is not torn down and rebuilt every render --
   * which would fire the callback again on each rebuild.
   */
  const loadMore = useCallback(() => {
    // Not from placeholder rows: their next page belongs to the previous search.
    if (list.isPlaceholderData) return;
    if (list.hasNextPage && !list.isFetchingNextPage) void list.fetchNextPage();
  }, [
    list.isPlaceholderData,
    list.hasNextPage,
    list.isFetchingNextPage,
    list.fetchNextPage,
  ]);

  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
    staleTime: 5 * 60_000,
  });
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });
  const channels = useQuery({
    queryKey: queryKeys.channels,
    queryFn: api.channels,
    staleTime: 5 * 60_000,
  });
  /*
   * Shares react-query's cache with TicketDetail, so it costs no extra request.
   * It exists so a pasted link to a ticket outside the current view still fills
   * the details panel instead of blanking it.
   */
  const detail = useQuery({
    queryKey: queryKeys.ticket(selectedId ?? 0),
    queryFn: () => api.ticket(selectedId!),
    enabled: inPreview,
    staleTime: 30_000,
  });

  /* Everybody on the open ticket, off the same cached conversation the thread
     pane is reading -- so the panel costs no request of its own. */
  const people = useThreadParticipants(
    detail.data?.messages,
    detail.data?.channel.address,
  );

  const tickets = useMemo(
    () => list.data?.pages.flatMap((page) => page.tickets) ?? [],
    [list.data],
  );

  /*
   * Opening a ticket reads it, here as well as on the server.
   *
   * Without this the row un-bolded only while it was the selected one and went
   * straight back to bold on the way out, because the list is a separate query
   * holding the unread set it was fetched with. Patching the cache is what
   * makes reading stick across views and until the next refetch -- which will
   * agree, since the server was told at the same moment.
   */
  useEffect(() => {
    if (selectedId === null) return;
    markTicketReadInLists(queryClient, selectedId);
  }, [selectedId, queryClient, list.data]);

  /*
   * Which rows are still waiting to be read. The open ticket is dropped from
   * the set immediately rather than waiting for the server: it is being read
   * right now, and watching the row you just clicked stay bold behind you reads
   * as the click not having registered.
   */
  const unread = useMemo(() => {
    // Unread rides per page, so the set is the union of every page loaded.
    const ids = new Set(
      list.data?.pages.flatMap((page) => page.unreadIds) ?? [],
    );
    if (selectedId !== null) ids.delete(selectedId);
    return ids;
  }, [list.data, selectedId]);

  const selected =
    tickets.find((ticket) => ticket.id === selectedId) ??
    detail.data?.ticket ??
    null;

  /*
   * The query string survives every move between the list and a ticket.
   *
   * Opening a result used to navigate to a bare /v/:view/:id, which dropped
   * `?q=` and therefore the search itself -- so picking the wrong result out of
   * ten meant retyping the search to get back to the other nine. The search is
   * part of where you are, not something you did on the way.
   */
  const search = useLocation().search;

  const openTicket = useCallback(
    (id: number) => {
      setDetailsOpen(false);
      navigate({ pathname: `/v/${view}/${id}`, search });
    },
    [navigate, view, search],
  );

  /*
   * A merged ticket redirects to the one it was merged into.
   *
   * The row is kept so old links still resolve -- a mail notification, a Lane
   * card, a bookmark -- but its messages have moved and every list filters it
   * out, so without this the link lands on a ticket with an empty thread and no
   * explanation. `replace`, because the merged id is not somewhere an agent
   * should be able to go Back to.
   */
  const mergedInto = detail.data?.mergedIntoTicketId ?? null;
  useEffect(() => {
    if (mergedInto === null) return;
    navigate({ pathname: `/v/${view}/${mergedInto}`, search }, { replace: true });
  }, [mergedInto, navigate, view, search]);

  /** Back to the list. The URL loses the ticket, so the layout follows. */
  const closeTicket = useCallback(
    () => navigate({ pathname: `/v/${view}`, search }),
    [navigate, view, search],
  );

  /*
   * Position in the current list, so an agent can work straight down a queue
   * without returning to it between tickets. -1 when the open ticket is not in
   * this view at all (a pasted link), which correctly disables both arrows.
   */
  const index = tickets.findIndex((ticket) => ticket.id === selectedId);
  const previous = index > 0 ? tickets[index - 1] : undefined;
  const next =
    index >= 0 && index < tickets.length - 1 ? tickets[index + 1] : undefined;

  /*
   * Escape returns to the list. Conventional for a reading pane, and it means
   * the way out is never further than the key under your finger.
   */
  useEffect(() => {
    if (!inPreview) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;

      // Not while typing a reply -- Escape belongs to the composer there, and
      // losing a half-written reply to a stray keypress is unforgivable.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }

      if (detailsOpen) {
        setDetailsOpen(false);
        return;
      }
      closeTicket();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inPreview, closeTicket, detailsOpen]);

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
    void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
    if (selectedId !== null) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(selectedId),
      });
    }
  }

  const setPipeline = useMutation({
    mutationFn: ({
      id,
      pipelineId,
    }: {
      id: number;
      pipelineId: number | null;
    }) => api.setPipeline(id, pipelineId),
    onSuccess: refresh,
  });

  const setAssignee = useMutation({
    mutationFn: ({
      id,
      assigneeId,
    }: {
      id: number;
      assigneeId: number | null;
    }) => api.setAssignee(id, assigneeId),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "open" | "closed" }) =>
      api.setStatus(id, status),
    onSuccess: refresh,
  });

  /*
   * Bulk selection, managers only.
   *
   * Kept here rather than in the list pane because it outlives a re-render of
   * the list and is what the action bar acts on. Cleared after a bulk action:
   * the tickets it named are no longer in the view, and a selection of rows
   * that are not on screen is a trap.
   */
  const canBulkEdit = user.role === "admin";
  const [checked, setChecked] = useState<Set<number>>(new Set());

  const toggleChecked = useCallback((id: number) => {
    setChecked((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const toggleAllChecked = useCallback((ids: number[]) => {
    setChecked((current) => {
      const all = ids.length > 0 && ids.every((id) => current.has(id));
      return all ? new Set() : new Set(ids);
    });
  }, []);

  // A selection made in one view means nothing in the next, and acting on it
  // there would close tickets the agent never saw.
  useEffect(() => setChecked(new Set()), [view, debouncedSearch]);

  /*
   * One mutation for all three actions, because what happens afterwards is the
   * same every time: drop the selection, refetch, and say so if anything was
   * left behind. `verb` is carried through only so the report can name what was
   * being done.
   */
  const bulkAction = useMutation({
    mutationFn: ({
      run,
    }: {
      verb: string;
      run: (ids: number[]) => Promise<BulkResult>;
      ids: number[];
    }) => run([...checked]),
    onSuccess: (result, { verb }) => {
      setChecked(new Set());
      refresh();

      if (result.failed.length > 0) {
        window.alert(
          `${verb} ${result.changed}. Could not ${verb.toLowerCase()} ` +
            `${result.failed.length}: ${result.failed
              .map((id) => `#${id}`)
              .join(", ")}`,
        );
      }
    },
    onError: (cause: unknown) =>
      window.alert(
        cause instanceof Error ? cause.message : "That did not work",
      ),
  });

  /**
   * Clicking a column title sorts by it; clicking the active one flips
   * direction. New columns open in the order that is useful first -- newest for
   * dates, most-urgent for SLA, A-Z for everything else.
   */
  function onSortChange(nextSort: TicketSort): void {
    if (nextSort === sort) {
      setDir((current: SortDirection) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(nextSort);
    setDir(nextSort === "last_message" ? "desc" : "asc");
  }

  const savedViews = useQuery({
    queryKey: queryKeys.savedViews,
    queryFn: api.savedViews,
    staleTime: 60_000,
  });

  /* A search has left the view behind (see `listParams`), so the heading must
     not still name it over results that are not in it. Spam keeps its name
     because the search there stays in spam. */
  const viewLabel =
    searching && view !== "spam"
      ? "Search results"
      : labelForView(view, users.data ?? [], savedViews.data ?? []);

  /*
   * The details column's width, dragged and remembered. Nothing in it wraps
   * gracefully -- a company name, an order reference and a Lane task id are
   * all one line or nothing -- so the right width is whatever the person
   * reading it says it is.
   */
  const details = usePaneSize("pulse.pane.details", 288, { min: 224, max: 560 });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        The view's name, its count and its filters, rendered up into the
        frame's header bar -- three stacked strips became one row. See
        `PageHeader` for why it is a portal.
      */}
      <PageHeader>
        {inPreview ? (
          <>
            <IconButton
              onClick={closeTicket}
              title="Back to list (Esc)"
              label="Back to list"
            >
              ←
            </IconButton>
            <span className="min-w-0 truncate text-sm font-medium text-content">
              {viewLabel}
            </span>
            {index >= 0 ? (
              <span className="hidden text-xs text-faint tabular-nums sm:inline">
                {index + 1} of {tickets.length}
              </span>
            ) : null}
          </>
        ) : (
          <>
            {/* Truncates because a saved view can be given any name, and the
                search box beside it must keep its width regardless. */}
            <h1 className="min-w-0 truncate text-sm font-medium text-content">
              {viewLabel}
            </h1>
            {/* A "+" because this is what is loaded, not what exists: the
                server sends no total, and printing a bare 100 next to a queue
                of 4,000 states something false. */}
            <span className="text-xs text-faint tabular-nums">
              {list.isLoading
                ? ""
                : `${tickets.length}${list.hasNextPage ? "+" : ""}`}
            </span>
          </>
        )}

        {/*
          Hidden on a phone while a ticket is open: these narrow the list, and
          the list is not on screen in the single-pane layout -- so it was a
          row of controls that changed nothing visible, on the width where the
          bar has least to spare.

          Before the pane controls, which take the far end of the row: what the
          filters narrow is the name they sit beside, and a control drifting to
          wherever the free space ended up is a control nobody finds twice in
          the same place.
        */}
        {/* Full width on a phone, so it takes a line of its own under the
            view's name rather than squeezing in beside it. */}
        <div className={cn("w-full min-w-0 md:w-auto", inPreview && "hidden md:block")}>
          <FilterBar
            filters={filters}
            onChange={setFilters}
            pipelines={pipelines.data ?? []}
            users={users.data ?? []}
            channels={channels.data ?? []}
            showAssignee={!viewFixesAssignee}
            searchSpansClosed={searching && view !== "spam"}
          />
        </div>

        {inPreview ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* Phone: the details column has nowhere to sit, so it opens
                over the thread instead. */}
            <button
              type="button"
              onClick={() => setDetailsOpen(true)}
              className={cn(
                "h-8 rounded-md border border-line px-2.5 text-sm text-content md:hidden",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
              )}
            >
              Details
            </button>

            <div className="hidden items-center gap-1 md:flex">
              <IconButton
                onClick={() => previous && openTicket(previous.id)}
                disabled={!previous}
                title="Previous ticket"
                label="Previous ticket"
              >
                ↑
              </IconButton>
              <IconButton
                onClick={() => next && openTicket(next.id)}
                disabled={!next}
                title="Next ticket"
                label="Next ticket"
              >
                ↓
              </IconButton>
            </div>

            {/*
              Beside the pane toggles, because it is the same kind of control:
              it acts on the conversation as a whole rather than on anything in
              it. Hidden until the thread has loaded -- a print button that
              produces an empty document is worse than one that arrives a
              moment late.
            */}
            {detail.data ? (
              <div className="hidden items-center md:flex">
                <PrintThreadButton detail={detail.data} />
              </div>
            ) : null}

            <div className="hidden items-center gap-1 md:flex">
              <IconButton
                onClick={() => setShowLeft((value) => !value)}
                pressed={showLeft}
                title={`${showLeft ? "Hide" : "Show"} ticket list`}
                label={`${showLeft ? "Hide" : "Show"} ticket list`}
              >
                {showLeft ? "◧" : "▢"}
              </IconButton>
              <IconButton
                onClick={() => setShowRight((value) => !value)}
                pressed={showRight}
                title={`${showRight ? "Hide" : "Show"} details`}
                label={`${showRight ? "Hide" : "Show"} details`}
              >
                {showRight ? "◨" : "▢"}
              </IconButton>
            </div>

            {/* Redundant on a phone, where the back arrow is the way out
                and every pixel of the header is contested. */}
            <span className="hidden md:block">
              <IconButton
                onClick={closeTicket}
                title="Close ticket (Esc)"
                label="Close ticket"
              >
                ✕
              </IconButton>
            </span>
          </div>
        ) : null}
      </PageHeader>

      {composing ? (
        <NewTicketDialog
          /* Remounted per draft, so resuming a second one cannot inherit the
             first's fields -- every value in there is read once on mount. */
          key={composing.draftId ?? "new"}
          draftId={composing.draftId}
          onClose={() => setComposing(null)}
        />
      ) : null}

      {inPreview ? (
        <div className="flex min-h-0 flex-1">
          {showLeft && isDesktop ? (
            <PreviewList
              tickets={tickets}
              pipelines={pipelines.data ?? []}
              users={users.data ?? []}
              calendar={calendar}
              selectedId={selectedId}
              unread={unread}
              loading={list.isLoading}
              hasMore={list.hasNextPage}
              loadingMore={list.isFetchingNextPage}
              onLoadMore={loadMore}
              onSelect={openTicket}
            />
          ) : null}

          {/* Keyed so switching tickets resets the reply draft -- a draft
              carried into the next customer's thread is unforgivable. */}
          <main className="min-w-0 flex-1 bg-surface">
            {selected || detail.isLoading ? (
              <TicketDetail
                key={selectedId}
                ticketId={selectedId!}
                currentUserId={user.id}
                sla={
                  selected ? (
                    <PhoneSla
                      ticket={selected}
                      pipelines={pipelines.data ?? []}
                      calendar={calendar}
                    />
                  ) : null
                }
              />
            ) : (
              <EmptyState
                title="Ticket not found"
                description="It may have been merged, or the link may be wrong."
              />
            )}
          </main>

          {selected && (isDesktop ? showRight : detailsOpen) ? (
            <>
              {/* The column's left border, made draggable. `invert` because
                  the handle is on the leading edge of what it sizes: dragging
                  left has to make the column wider, not narrower. */}
              {isDesktop ? (
                <ResizeHandle
                  axis="x"
                  invert
                  label="Ticket details"
                  size={details.size}
                  min={224}
                  max={560}
                  onChange={details.setSize}
                  onReset={details.reset}
                />
              ) : null}

              <aside
                style={isDesktop ? { width: details.size } : undefined}
                className={cn(
                  isDesktop
                    ? "shrink-0"
                    : // Full screen on a phone, and a grid so the pane below the
                      // bar gets a definite height to scroll inside.
                      "fixed inset-0 z-40 grid grid-rows-[auto_minmax(0,1fr)] bg-surface",
                )}
              >
                {isDesktop ? null : (
                  <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                    <span className="text-sm font-medium text-content">
                      Ticket details
                    </span>
                    <button
                      type="button"
                      onClick={() => setDetailsOpen(false)}
                      aria-label="Close details"
                      className="ml-auto flex size-8 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <TicketDetailsPane
                  ticket={selected}
                  people={people}
                  pipelines={pipelines.data ?? []}
                  users={users.data ?? []}
                  calendar={calendar}
                  onPipelineChange={(pipelineId) =>
                    setPipeline.mutate({ id: selected.id, pipelineId })
                  }
                  onAssigneeChange={(assigneeId) =>
                    setAssignee.mutate({ id: selected.id, assigneeId })
                  }
                  onStatusChange={(status) =>
                    setStatus.mutate({ id: selected.id, status })
                  }
                  onOpenTicket={openTicket}
                  currentUserId={user.id}
                />
              </aside>
            </>
          ) : null}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/*
            Only once something is ticked. A permanently visible bar would be a
            row of disabled buttons above every queue, and the selection is the
            only thing that makes any of them meaningful.
          */}
          {canBulkEdit && checked.size > 0 ? (
            /*
             * Directly under the filters and hard against the left, where the
             * count is. The first version pushed the one action to the far
             * right with ml-auto, which on a wide screen put it a monitor's
             * width from the thing it applied to -- and it was reported as not
             * existing.
             */
            <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2 px-3 py-1.5">
              <span className="text-xs font-medium text-content">
                {checked.size} selected
              </span>

              <BulkSelect
                label="Assign to"
                disabled={bulkAction.isPending}
                options={[
                  { value: "none", label: "Unassigned" },
                  ...(users.data ?? []).map((entry) => ({
                    value: String(entry.id),
                    label: entry.name,
                  })),
                ]}
                onPick={(raw) =>
                  bulkAction.mutate({
                    verb: "Assigned",
                    ids: [...checked],
                    run: (ids) =>
                      api.bulkAssignTickets(
                        ids,
                        raw === "none" ? null : Number(raw),
                      ),
                  })
                }
              />

              <BulkSelect
                label="Pipeline"
                disabled={bulkAction.isPending}
                options={[
                  { value: "none", label: "No pipeline" },
                  ...(pipelines.data ?? []).map((entry) => ({
                    value: String(entry.id),
                    label: entry.name,
                  })),
                ]}
                onPick={(raw) =>
                  bulkAction.mutate({
                    verb: "Moved",
                    ids: [...checked],
                    run: (ids) =>
                      api.bulkPipelineTickets(
                        ids,
                        raw === "none" ? null : Number(raw),
                      ),
                  })
                }
              />

              <button
                type="button"
                disabled={bulkAction.isPending}
                onClick={() => {
                  /*
                   * Asked before doing, and only this one: closing is the
                   * destructive action of the three, and the count is the part
                   * somebody needs to see before agreeing. Assigning and
                   * reclassifying are both undone by doing them again.
                   */
                  const many = checked.size === 1 ? "ticket" : "tickets";
                  if (!window.confirm(`Close ${checked.size} ${many}?`)) return;

                  bulkAction.mutate({
                    verb: "Closed",
                    ids: [...checked],
                    run: (ids) => api.bulkCloseTickets(ids),
                  });
                }}
                className={cn(
                  "rounded-md border border-line bg-surface px-2 py-1 text-xs text-content",
                  "hover:bg-surface-2 disabled:opacity-50",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                )}
              >
                Close
              </button>

              <button
                type="button"
                onClick={() => setChecked(new Set())}
                className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
              >
                Clear
              </button>

              {bulkAction.isPending ? (
                <span className="text-xs text-muted">Working…</span>
              ) : null}
            </div>
          ) : null}

          {/*
            The ticket that was never raised.
            
            It cannot be a row in the list below, because the list is tickets
            and this is not one yet -- there is no id to fetch, no contact, no
            SLA. So it sits above as a bar rather than being forced into a
            shape it does not have, and it is here rather than on a page of its
            own because "what have I not sent" is one question.
          */}
          {draftsView && unsentNew.length > 0 ? (
            <div className="border-b border-line">
              {unsentNew.map(({ id, draft }) => (
                <div
                  key={id}
                  className="flex items-center gap-3 border-b border-line bg-surface-2 px-3 py-2 last:border-0"
                >
                  <span className="shrink-0 rounded bg-surface px-1.5 py-0.5 text-[11px] font-medium text-muted">
                    Unsent
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-content">
                    {draft.subject.trim() || "New ticket"}
                    <span className="text-muted">
                      {draft.to[0]
                        ? ` — to ${draft.to[0].name || draft.to[0].email}`
                        : " — no recipient yet"}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setComposing({ draftId: id })}
                    className="shrink-0 rounded-md border border-line bg-surface px-2 py-1 text-xs text-content hover:bg-surface-2"
                  >
                    Resume
                  </button>
                  <button
                    type="button"
                    onClick={() => clearNewTicketDraft(id)}
                    className="shrink-0 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    Discard
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {/*
            The pane sizes itself with h-full, which is 100% of this column --
            so with the bar above it the list ran past the bottom of the window
            by the bar's height. Giving it its own flex row to fill leaves the
            pane's own sizing alone.
          */}
          <div className="min-h-0 flex-1">
            <TicketListPane
              tickets={tickets}
              pipelines={pipelines.data ?? []}
              users={users.data ?? []}
              calendar={calendar}
              selectedId={selectedId}
              unread={unread}
              sort={sort}
              dir={dir}
              loading={list.isLoading}
              hasMore={list.hasNextPage}
              loadingMore={list.isFetchingNextPage}
              onLoadMore={loadMore}
              onSortChange={onSortChange}
              onSelect={openTicket}
              onAssigneeChange={(id, assigneeId) =>
                setAssignee.mutate({ id, assigneeId })
              }
              onPipelineChange={(id, pipelineId) =>
                setPipeline.mutate({ id, pipelineId })
              }
              selectable={canBulkEdit}
              checked={checked}
              onToggle={toggleChecked}
              onToggleAll={toggleAllChecked}
              currentUserId={user.id}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A one-shot action in the shape of a dropdown.
 *
 * Snaps back to its label after every pick, because it is a verb rather than a
 * setting: the selection it acted on is gone the moment it runs, so a box left
 * reading "Assign to Sam" would be describing tickets that are no longer
 * selected -- and would look like a filter.
 */
function BulkSelect({
  label,
  options,
  disabled,
  onPick,
}: {
  label: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onPick: (value: string) => void;
}) {
  return (
    <select
      value=""
      disabled={disabled}
      aria-label={label}
      onChange={(event) => {
        const picked = event.target.value;
        // Reset first: the handler clears the selection and this component
        // may re-render before the browser would have restored it.
        event.target.value = "";
        if (picked) onPick(picked);
      }}
      className={cn(
        "rounded-md border border-line bg-surface px-2 py-1 text-xs text-content",
        "hover:bg-surface-2 disabled:opacity-50",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
      )}
    >
      <option value="">{label}…</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function IconButton({
  children,
  onClick,
  title,
  label,
  disabled,
  pressed,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  label: string;
  disabled?: boolean;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      {...(pressed === undefined ? {} : { "aria-pressed": pressed })}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded border border-line text-xs",
        /* The back arrow is the only one of these a phone shows, and at 24px
           it was the smallest target on the screen for the commonest tap. */
        "max-md:h-9 max-md:w-9 max-md:text-base",
        "transition-colors hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        disabled
          ? "cursor-not-allowed text-faint opacity-40"
          : pressed === false
            ? "text-faint"
            : "text-content",
      )}
    >
      {children}
    </button>
  );
}

/** The live SLA for the phone's ticket bar. A component for its hook. */
function PhoneSla({
  ticket,
  pipelines,
  calendar,
}: {
  ticket: TicketSummary;
  pipelines: Pipeline[];
  calendar: BusinessCalendar;
}) {
  const sla = useLiveSla(ticket, pipelines, calendar);
  return <SlaLeft sla={sla} />;
}
